import {
  ChangeDetectionStrategy,
  Component,
  signal,
  Type,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { getNestedValue } from '@lib/misc';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyFieldSelectProps } from '@ngx-formly/core/select';
import { debounceTime, map, Observable, of, Subject, switchMap } from 'rxjs';
import { FormlyFieldProps } from '../form-field/form-field.wrapper';

interface SelectProps extends FormlyFieldProps, FormlyFieldSelectProps {
  appendTo?: any;
  filterable?: boolean;
  optionLabel?: string;
  optionValue?: string;
  filter: (term: any) => any;
  apiCall: (query: string) => Observable<any[]>;
  apiCallSingle?: (id: string | number) => Observable<any>;
}

export interface FormlySelectFieldConfig
  extends FormlyFieldConfig<SelectProps> {
  type: 'select' | Type<FormlyFieldApiSelect>;
}

@Component({
  selector: 'formly-field-primeng-api-select',
  template: `
    <p-select
      [appendTo]="props.appendTo"
      [placeholder]="props.placeholder"
      [options]="(optionLoader | async) ?? []"
      [formControl]="formControl"
      [formlyAttributes]="field"
      [showClear]="!props.required"
      [filter]="props.filterable"
      [loading]="loading()"
      (onClear)="clearFilter()"
      (onChange)="props.change && props.change(field, $event)"
    >
      <ng-template pTemplate="filter">
        <p-iconfield>
          <input type="text" pInputText [formControl]="filterControl" />
          <p-inputicon styleClass="pi pi-search" />
        </p-iconfield>
      </ng-template>
    </p-select>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldApiSelect extends FieldType<any> {
  searchSubject = new Subject<string>();
  optionLoader: Observable<any[]>;
  loading = signal(false);
  filterControl = new FormControl('');
  preSelectedOption: any = null;

  constructor() {
    super();
  }

  ngOnInit() {
    // Check if there's an initial value and apiCallSingle is available
    if (this.formControl.value && this.props.apiCallSingle) {
      this.loadPreSelectedOption();
    }

    this.optionLoader = this.loadOptions('');

    // Debounced API filtering
    this.searchSubject
      .pipe(
        debounceTime(100),
        switchMap(query => this.loadOptions(query))
      )
      .subscribe(data => {
        this.optionLoader = of(data);
      });

    this.filterControl.valueChanges.pipe(debounceTime(100)).subscribe(value => {
      this.searchSubject.next(value ?? '');
    });
    // TODO : This is not working
    // (this.field as any).componentRef = this;
  }

  refreshOptions() {
    this.optionLoader = this.loadOptions('');
  }

  clearFilter() {
    this.filterControl.setValue('');
  }

  loadPreSelectedOption(): void {
    if (this.props.apiCallSingle) {
      this.props.apiCallSingle(this.formControl.value).subscribe({
        next: (item: any) => {
          // Ensure consistent data types - convert form control value to string to match options
          const stringValue = item.id.toString();
          this.preSelectedOption = {
            label: this.getLabelOption(item),
            value: stringValue,
          };

          // Also update the form control to use string value for consistency
          if (this.formControl.value !== stringValue) {
            this.formControl.setValue(stringValue, { emitEvent: false });
          }

          // Immediately update the options to include the pre-selected option
          this.optionLoader = this.loadOptions('').pipe(
            map((options: any[]) => {
              // Make sure the pre-selected option is at the top
              if (
                this.preSelectedOption &&
                !options.some(
                  (option: any) => option.value === this.preSelectedOption.value
                )
              ) {
                return [this.preSelectedOption, ...options];
              }
              return options;
            })
          );
        },
        error: (error: any) => {
          console.error('Failed to load pre-selected option:', error);
        },
      });
    }
  }

  ngAfterViewInit() {}

  loadOptions(query: string): Observable<any[]> {
    query = query.toLowerCase();

    this.loading.set(true);
    if (!this.props.apiCall) {
      const items = (this.props.options as string[])!.filter(item => {
        if (!query) return true;

        const label = getNestedValue(
          item,
          this.props?.optionLabel ?? 'label',
          ''
        )!
          .toLowerCase()
          .includes(query.toLowerCase());

        return label;
      });

      this.loading.set(false);
      return of(items);
    }

    this.loading.set(true);
    return this.props.apiCall(query).pipe(
      map((response: any) => {
        this.loading.set(false);
        const options = response.data.map((item: any) => {
          return {
            label: this.getLabelOption(item),
            value: item.id.toString(),
          };
        });

        // Add pre-selected option if it exists and is not already in the results
        if (
          this.preSelectedOption &&
          !options.some(
            (option: any) => option.value === this.preSelectedOption.value
          )
        ) {
          options.unshift(this.preSelectedOption);
        }

        return options;
      })
    );
  }

  getLabelOption(object: Record<string, any>): string {
    return this.props
      .optionLabel!.split(' ')
      .map((item: any) => getNestedValue(object, item, ''))
      .join(' ');
  }
}
